import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { useTRPC } from "@/trpc";
import { useMutation } from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import { toast } from "sonner";
import * as Icons from "lucide-react"
import { useDebounce } from "@uidotdev/usehooks";

interface Props {
    value?: string;
    onValueChange?: (value:string | undefined) => void;
}

const SelectContact = ({value, onValueChange}:Props) => {
    const { trpc } = useTRPC();
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const debouncedSearch = useDebounce(search, 500);
    const [selectedValue, setselectedValue] = React.useState<string | undefined>(value);

    const { mutate:searchContacts, data:contacts, isLoading } = useMutation({
        mutationFn: async (data:{search:string, id?:string}) => {
            const contacts = await trpc.contacts.search.query({
                search: data.search,
                id: data.id
            })
            return contacts;
        },
        onError: (err) => {
            if(err instanceof Error) {
                toast.error(err.message);
            } else if(err instanceof TRPCClientError) {
                toast.error(err.message);
            } else {
                toast.error("Er is een onbekende fout opgetreden.");
            }
        }
    })

    useEffect(() => {
        searchContacts({search});
    }, [debouncedSearch])

    useEffect(()=>{
        if(!value) return;
        setselectedValue(value);
        searchContacts({search, id:value});
    }, [value])

    const handleSelect = (id:string | undefined) => {
        if(selectedValue === id) {
            setselectedValue(undefined);
            onValueChange?.(undefined);
        } else {
            setselectedValue(id);
            onValueChange?.(id);
        }
        
        setOpen(false);
    }

    const selected = contacts?.find(u => u.id === selectedValue);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
            <Button variant='outline' className="w-full">
                {selected
                ? `${selected.contact_name}`
                : 'Selecteer contact...'
                }
            </Button>            
        </DialogTrigger>
        <DialogContent className="p-0 rounded-3xl gap-0 overflow-hidden" hideCloseButton>
            <div className="flex items-center relative">
                <input 
                    value={search} 
                    onChange={(e)=>{setSearch(e.currentTarget.value)}} 
                    placeholder="Zoek naar contact" 
                    className="focus:outline-none py-3 px-4 bg-input/50 rounded-3xl text-sm w-full h-12"
                />
                <button onClick={()=>{setOpen(false)}} className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground size-12 aspect-square hover:bg-muted rounded-full flex items-center justify-center">
                    <Icons.X className="w-4 h-4 text-muted-foreground"/>
                </button>
            </div>
            
            {isLoading && <p className="text-center text-sm text-muted-foreground my-2">Bezig met zoeken...</p>}

            {(contacts && contacts?.length > 0) && 
            <div className="max-h-96 overflow-auto flex flex-col gap-1 p-2">
                {contacts?.map((contact)=>(
                    <button onClick={()=>{handleSelect(contact.id)}} className="flex items-center justify-between gap-4 p-2 rounded-xl hover:bg-muted text-left" key={contact.id}>
                        <div className="flex items-center gap-2">
                            <div>
                                <p className="font-medium">{contact?.contact_name}</p>
                            </div>
                        </div>
                        
                        {contact.id === selectedValue && <Icons.Check className="w-4 h-4 text-foreground"/>}
                    </button>
                ))}
            </div>
            }
        </DialogContent>
    </Dialog>
  )
}

export default SelectContact